import type { LayoutSpec, PanelComponentSpec } from '@src/types/LayoutConfig';
import { Box, HStack, Icon, VStack } from '@talos/kyoko';
import type { DockviewApi, IDockviewDefaultTabProps, IDockviewPanelProps } from 'dockview';
import { DockviewReact, type DockviewReadyEvent, themeDark as dockviewThemeDark } from 'dockview';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { PANEL_COMPONENTS_LIST } from '../AppLayout/Panels';
import { ErrorBoundary } from '../ErrorBoundary';
import { TAB_COMPONENTS } from '../Layout/Components/TabComponents';
import { panelPreviewBackground, PreviewLayoutWrapper, previewTabBackground } from './styles';

export function AppLayoutPreview({
  layoutSpec,
  onReady,
}: {
  layoutSpec: LayoutSpec;
  onReady?: (api: DockviewApi) => void;
}) {
  const theme = useTheme();
  const darkTheme = useMemo(
    () => ({
      ...dockviewThemeDark,
      gap: theme.spacingLayout,
    }),
    [theme]
  );

  const previewComponents = useMemo(() => {
    return PANEL_COMPONENTS_LIST.reduce(
      (res: Record<string, FunctionComponent<IDockviewPanelProps>>, component: PanelComponentSpec) => {
        res[component.type] = () => (
          <VStack
            background={panelPreviewBackground(theme, component.type)}
            h="100%"
            w="100%"
            container="panel / inline-size"
            data-testid="panel-preview-content"
          >
            {component.icon && <Icon color="colorTextMuted" size={16} icon={component.icon} />}
            <Box mt="spacing4" textAlign="center" color="colorTextDefault" fontSize="clamp(0.5rem, 10cqi, 0.75rem)">
              {component.label}
            </Box>
          </VStack>
        );
        return res;
      },
      {}
    );
  }, [theme]);

  const handleReady = useCallback(
    (event: DockviewReadyEvent) => {
      onReady?.(event.api);
      event.api.fromJSON(layoutSpec.dockViewLayout);
      // Adjust all minwidth and minheight constraints
      event.api.groups.forEach(group => {
        group.api.setConstraints({
          minimumWidth: 0,
          minimumHeight: 60,
        });
      });
    },
    [layoutSpec.dockViewLayout, onReady]
  );

  return (
    <ErrorBoundary>
      <PreviewLayoutWrapper
        minHeight="160px"
        aspectRatio="3/2"
        data-testid="app-layout-preview"
        data-layout-id={layoutSpec.id}
      >
        <DockviewReact
          onReady={handleReady}
          theme={darkTheme}
          components={previewComponents}
          defaultTabComponent={PreviewTabComponent}
          tabComponents={PREVIEW_TAB_COMPONENTS}
        />
      </PreviewLayoutWrapper>
    </ErrorBoundary>
  );
}

function PreviewTabComponent() {
  const theme = useTheme();
  return (
    <HStack p="spacing4" background={previewTabBackground(theme)} fontSize={0.5} h="100%">
      <Box background="colors.gray.070" w="12px" borderRadius="borderRadiusDefault" h="100%" />
    </HStack>
  );
}

const PREVIEW_TAB_COMPONENTS = Object.keys(TAB_COMPONENTS).reduce(
  (res: Record<string, FunctionComponent<IDockviewDefaultTabProps>>, key: string) => {
    res[key] = PreviewTabComponent;
    return res;
  },
  {}
);
